import { Instance, SnapshotOut, types } from 'mobx-state-tree'
import { addPushPermissionListener, isPushGranted, optInPush } from '@/modules/push'

import { extendLogger, LoggerContexts } from '../../utils/logger'
import { withRootStore } from '../extensions'
import { isPlatformPaySupported } from './isPlatformPaySupported'

const logger = extendLogger(LoggerContexts.PermissionsStore)

/**
 * A PermissionsStore model.
 */

export const PermissionsStore = types
  .model('PermissionsStore')
  .props({
    isPushDisabled: types.optional(types.boolean, false),
    isSoundEnabled: types.optional(types.boolean, true),
    isNativePaymentSupported: types.optional(types.boolean, false),
    pushPermissionRequestedAt: types.optional(types.number, 0),
    shouldShowWebNotificationPrompt: types.optional(types.boolean, false)
  })
  .extend(withRootStore)
  .actions((self) => ({
    setIsPushDisabled(value: boolean) {
      self.isPushDisabled = value
    },
    setIsSoundEnabled(value: boolean) {
      self.isSoundEnabled = value
    },
    setIsNativePaymentSupported(value: boolean) {
      self.isNativePaymentSupported = value
    },
    setPushPermissionRequestedAt(value: number) {
      self.pushPermissionRequestedAt = value
    },
    setShouldShowWebNotificationPrompt(value: boolean) {
      self.shouldShowWebNotificationPrompt = value
    }
  }))
  .views((self) => ({
    get pushPermissionExpired() {
      const hoursToExpire = 4
      return self.pushPermissionRequestedAt + 1000 * 60 * 60 * hoursToExpire < Date.now()
    }
  }))
  .actions((self) => ({
    async refresh() {
      const hasPermission = await isPushGranted()
      const user = self.rootStore.session?.user?._id
      const isPNSheetOpen = self.rootStore.session?.isPNSheetOpen

      if (isPNSheetOpen) {
        setTimeout(() => {
          if (user && hasPermission) {
            // Opts in on the token owner, then fans the token out to every provider.
            void optInPush()
          }
        }, 500)
      }
      self.setIsPushDisabled(!hasPermission)
    },
    async afterAttach() {
      const isSupported = await isPlatformPaySupported()

      console.log({ isNativePaymentSupported: isSupported })
      self.setIsNativePaymentSupported(isSupported)

      const permissionChangedHandler = async (hasPermission: boolean) => {
        logger.debug('PermissionsStore.permissionChangedHandler hasPermission?', hasPermission)

        if (hasPermission) {
          await optInPush()
        }

        self.setIsPushDisabled(!hasPermission)
      }
      addPushPermissionListener(permissionChangedHandler)
    }
  }))

/**
 * The PermissionsStore instance.
 */
export type PermissionsStoreType = Instance<typeof PermissionsStore>

/**
 * The data of a PermissionsStore.
 */
export type PermissionsStoreSnapshot = SnapshotOut<typeof PermissionsStore>
